"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import { useConnectTelegramMessagingChannelMutation } from "@/hooks/mutations/messaging-channels"
import { useTranslations } from "next-intl"



interface TelegramConnectDialogProps {
  agentId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after a successful connect so the parent can refresh the channel list. */
  onConnected: () => void
}

/**
 * Token-paste connect flow for Telegram bots — no OAuth popup. The user
 * creates a bot via @BotFather and pastes its token here; the server
 * validates the token and registers the webhook.
 */
export function TelegramConnectDialog({
  agentId,
  open,
  onOpenChange,
  onConnected,
}: TelegramConnectDialogProps) {

  const tUi = useTranslations("Messaging.AgentsMessagingSettingsTelegramConnectDialog")
  const tCommonUi = useTranslations("Common")

  const [botToken, setBotToken] = useState("")
  const [error, setError] = useState<string | null>(null)
  const connect = useConnectTelegramMessagingChannelMutation()
  const isConnecting = connect.isPending

  const handleOpenChange = (next: boolean) => {
    if (isConnecting) return
    if (!next) {
      setBotToken("")
      setError(null)
    }
    onOpenChange(next)
  }

  const handleConnect = async () => {
    if (!botToken.trim()) {
      setError(tUi("pasteTheBotTokenFirst"))
      return
    }
    setError(null)
    try {
      await connect.mutateAsync({ agentId, botToken: botToken.trim() })

      toast.success(tUi("telegramBotConnectedSuccessfully"))
      setBotToken("")
      onOpenChange(false)
      onConnected()
    } catch (connectError) {
      setError(
        connectError instanceof Error
          ? connectError.message
          : tUi("failedToConnectTelegramBot"),
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle><span key={tUi("connectTelegramBot")}>{tUi("connectTelegramBot")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("customersWillChatWithYourBotInTelegram")}>{tUi("customersWillChatWithYourBotInTelegram")}</span></DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-600">
            <li>
              <span><span key={tUi("open")}>{tUi("open")}</span></span>{" "}
              <a
                href="https://t.me/botfather"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                <span key={"@BotFather"}>{"@BotFather"}</span></a>{" "}
              <span><span key={tUi("inTelegram")}>{tUi("inTelegram")}</span></span>
            </li>
            <li>
              <span><span key={tUi("send")}>{tUi("send")}</span></span> <code className="bg-gray-100 px-1 rounded">/newbot</code> <span><span key={tUi("andFollowTheStepsOr")}>{tUi("andFollowTheStepsOr")}</span></span> <code className="bg-gray-100 px-1 rounded">/token</code> <span><span key={tUi("forAnExistingBot")}>{tUi("forAnExistingBot")}</span></span>
            </li>
            <li><span key={tUi("copyTheTokenAndPasteItBelow")}>{tUi("copyTheTokenAndPasteItBelow")}</span></li>
          </ol>

          <div className="space-y-2">
            <Label htmlFor="telegram-bot-token"><span key={tUi("botToken")}>{tUi("botToken")}</span></Label>
            <Input
              id="telegram-bot-token"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder={"123456789:AAEhB0xZ..."}
              className="font-mono"
              autoComplete="off"
              disabled={isConnecting}
            />
          </div>

          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isConnecting}>
            <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          <Button onClick={handleConnect} disabled={isConnecting || !botToken.trim()}>
            {isConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            <span><span key={tUi("connect")}>{tUi("connect")}</span></span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
